import { useState, useEffect, useCallback, useRef } from "react";
import { todayISO } from "@/lib/utils";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import { Modal } from "@/components/ui/modal";
import ConfirmModal from "@/components/ui/modal/ConfirmModal";
import { extractPaidBillWarning } from "@/lib/paidBillWarning";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { manureSaleService } from "@/services/manureSaleService";
import { useSalePayments, PAYMENTS_EXCEED_MSG } from "@/hooks/useSalePayments";
import SalePaymentSection from "@/components/sales/SalePaymentSection";
import BuyerCreditNotice from "@/components/sales/BuyerCreditNotice";
import { buyerService } from "@/services/masterDataService";
import { handleApiError } from "@/services/api";
import DateRangeFilter, { getDefaultDateRange } from "@/components/common/DateRangeFilter";
import PrintLink from "@/components/print/PrintLink";
import { openPrintWindow, writeAndPrint } from "@/utils/printInvoice";
import { amountToWords } from "@/utils/numberToWords";
import { stateFromGSTIN } from "@/utils/gstinStates";
import type { ManureSale, Buyer } from "@/types/models";
import { useIsViewer } from "@/hooks/useRole";
import { useSortable } from "@/hooks/useSortable";
import SortableHeader from "@/components/ui/table/SortableHeader";
import { useAuthStore } from "@/store/useAuthStore";
import Pagination from "@/components/ui/pagination/Pagination";
import { deleteSaleMessage } from "./deleteSaleMessage";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

const UNIT_OPTIONS = ["load", "kg", "tonne", "trolley"] as const;

const ADHOC_BUYER = "__adhoc__";

export default function ManureSalesPage() {
  const isViewer = useIsViewer();
  const { sortConfig, requestSort } = useSortable("date", "desc");
  const [entries, setEntries] = useState<ManureSale[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [filterBuyers, setFilterBuyers] = useState<Buyer[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  // Set when the server asked "money has been paid on this sale"; the next
  // press of Delete is the owner saying yes.
  const [paidWarning, setPaidWarning] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [filterFrom, setFilterFrom] = useState(() => getDefaultDateRange().from);
  const [filterTo, setFilterTo] = useState(() => getDefaultDateRange().to);
  const [filterBuyer, setFilterBuyer] = useState("");

  // Form state
  const [date, setDate] = useState(todayISO());
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("load");
  const [ratePerUnit, setRatePerUnit] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  const [buyer, setBuyer] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [notes, setNotes] = useState("");
  // Split-tender payments (create only; a real buyer, not the adhoc walk-in).
  const invoiceTotal = Math.round(parseFloat(totalAmount) || 0);
  const pay = useSalePayments(invoiceTotal);
  // Owner's explicit approval for a sale that breaches the buyer's credit terms.
  const [creditOverride, setCreditOverride] = useState(false);
  const showPayments = !editingId && buyer !== "" && buyer !== ADHOC_BUYER;
  const paymentsExceedTotal = showPayments && pay.exceedsTotal;

  useEffect(() => {
    buyerService.list().then((data) => {
      // The sale form offers active buyers; the FILTER offers every buyer of
      // this kind, inactive ones too — their past sales are still in the list.
      const ofKind = (data as Buyer[]).filter((b) => b.categories?.includes("manure"));
      setBuyers(ofKind.filter((b) => b.status === "active"));
      setFilterBuyers(ofKind);
    }).catch(() => {});
  }, []);

  const loadSales = useCallback(async (fromDate?: string, toDate?: string, newOffset?: number) => {
    setError("");
    try {
      const o = newOffset !== undefined ? newOffset : offset;
      const params: Record<string, string | number> = { limit, offset: o };
      const f = fromDate !== undefined ? fromDate : filterFrom;
      const t = toDate !== undefined ? toDate : filterTo;
      if (f) params.from = f;
      if (t) params.to = t;
      if (filterBuyer) params.buyer_id = filterBuyer;
      params.sort_by = sortConfig.key;
      params.sort_order = sortConfig.direction;
      const res = await manureSaleService.list(params);
      if (Array.isArray(res)) {
        setEntries(res as unknown as ManureSale[]);
        setTotal((res as unknown as ManureSale[]).length);
      } else {
        const paginated = res as unknown as {
          data: ManureSale[];
          meta: { total: number };
        };
        setEntries(paginated.data ?? []);
        setTotal(paginated.meta?.total ?? 0);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [offset, filterFrom, filterTo, filterBuyer, sortConfig]);

  useEffect(() => {
    loadSales();
  }, [loadSales]);

  // Skips the auto-calc for the one render after openEdit hydrates the form, so
  // editing an unrelated field doesn't overwrite a stored total that was
  // manually adjusted away from qty×rate.
  // Edge: two back-to-back edits of records with identical inputs leave the flag
  // set for one extra render; it self-clears on the next change, never corrupting
  // a stored total.
  const skipAutoCalcRef = useRef(false);

  useEffect(() => {
    if (skipAutoCalcRef.current) {
      skipAutoCalcRef.current = false;
      return;
    }
    if (qty && ratePerUnit) {
      const q = parseFloat(qty);
      const r = parseFloat(ratePerUnit);
      if (q > 0 && r > 0) {
        setTotalAmount(String(Math.round(q * r)));
      }
    }
  }, [qty, ratePerUnit]);

  const openCreate = () => {
    setEditingId(null);
    setDate(todayISO());
    setQty("");
    setUnit("load");
    setRatePerUnit("");
    setTotalAmount("");
    setBuyer("");
    setBuyerName("");
    setNotes("");
    pay.reset();
    setError("");
    setModalOpen(true);
  };

  const openEdit = (sale: ManureSale) => {
    skipAutoCalcRef.current = true;
    setEditingId(sale.id);
    setDate(sale.date.split("T")[0]);
    setQty(String(sale.qty));
    setUnit(sale.unit);
    setRatePerUnit(String(sale.rate_per_unit));
    setTotalAmount(String(Math.round(sale.total_amount)));
    if (sale.buyer_id) {
      setBuyer(String(sale.buyer_id));
      setBuyerName("");
    } else if (sale.buyer) {
      setBuyer(ADHOC_BUYER);
      setBuyerName(sale.buyer);
    } else {
      setBuyer("");
      setBuyerName("");
    }
    setNotes(sale.notes || "");
    pay.reset(); // payments are create-only
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const data = {
        date,
        qty: parseFloat(qty),
        unit,
        rate_per_unit: parseFloat(ratePerUnit),
        total_amount: Math.round(parseFloat(totalAmount)),
        buyer_id: buyer && buyer !== ADHOC_BUYER ? parseInt(buyer) : undefined,
        buyer: buyer === ADHOC_BUYER ? buyerName.trim() : undefined,
        credit_override: creditOverride,
        notes: notes || undefined,
      };
      if (editingId) {
        await manureSaleService.update(editingId, data);
      } else {
        const lines = showPayments ? pay.buildLines() : [];
        if (paymentsExceedTotal) {
          setError(PAYMENTS_EXCEED_MSG);
          setSaving(false);
          return;
        }
        await manureSaleService.create({ ...data, payments: lines.length ? lines : undefined });
      }
      setModalOpen(false);
      setLoading(true);
      await loadSales();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const closeDelete = () => {
    setDeleteConfirmId(null);
    setPaidWarning(null);
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    setDeleting(true);
    try {
      await manureSaleService.delete(deleteConfirmId, paidWarning !== null);
      closeDelete();
      setLoading(true);
      await loadSales();
    } catch (err) {
      const warning = extractPaidBillWarning(err);
      if (warning) {
        setPaidWarning(warning);
        return;
      }
      setError(handleApiError(err));
      closeDelete();
    } finally {
      setDeleting(false);
    }
  };

  const handleDateRangeApply = (from: string, to: string) => {
    setFilterFrom(from);
    setFilterTo(to);
    setOffset(0);
    setLoading(true);
    loadSales(from, to, 0);
  };

  const handlePrev = () => {
    const newOffset = Math.max(0, offset - limit);
    setOffset(newOffset);
    loadSales(undefined, undefined, newOffset);
  };

  const handleNext = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    loadSales(undefined, undefined, newOffset);
  };

  const farm = useAuthStore((s) => s.farm);
  const [printingId, setPrintingId] = useState<number | null>(null);

  const formatCurrency = (amount: number) =>
    amount.toLocaleString("en-IN", { maximumFractionDigits: 2 });

  const handlePrint = async (sale: ManureSale) => {
    const pw = openPrintWindow();
    if (!pw) return;
    setPrintingId(sale.id);
    try {
      let buyer: Buyer | null = null;
      if (sale.buyer_id) {
        try { buyer = await buyerService.get(sale.buyer_id); } catch { /* proceed without */ }
      }
      const buyerGstin = (buyer?.details as Record<string, string> | undefined)?.gstNumber;
      const buyerState = stateFromGSTIN(buyerGstin);
      const inv = farm?.invoice_settings;
      writeAndPrint(pw, {
        farmName: farm?.name || "Farm",
        productionAddress: farm?.production_address,
        registeredAddress: farm?.address,
        stateName: farm?.state_name,
        stateCode: farm?.state_code,
        msmeNumber: farm?.msme_number,
        panNumber: farm?.pan_number,
        contactPhone: farm?.phone,
        email: farm?.email,
        farmGst: farm?.gst_number,
        invoiceNumber: `MNR-${String(sale.id).padStart(5, "0")}`,
        invoiceDate: new Date(sale.date).toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }),
        buyerName: buyer?.name || sale.buyer_name || sale.buyer,
        buyerAddress: buyer?.address ?? undefined,
        buyerGstin,
        buyerStateName: buyerState?.name,
        buyerStateCode: buyerState?.code,
        lineItems: [{
          sno: 1,
          description: "Manure",
          qty: `${sale.qty} ${sale.unit}${sale.qty > 1 ? "s" : ""}`,
          rate: `${formatCurrency(sale.rate_per_unit)}/${sale.unit}`,
          amount: formatCurrency(sale.total_amount),
        }],
        totalAmount: formatCurrency(sale.total_amount),
        amountInWords: amountToWords(sale.total_amount),
        bankName: inv?.bank_name,
        bankAccountName: inv?.bank_account_name,
        bankAccountNumber: inv?.bank_account_number,
        bankBranchIfsc: inv?.bank_branch_ifsc,
        declarationText: inv?.declaration_text,
        jurisdictionText: inv?.jurisdiction_text,
        signatureUrl: inv?.signature_url,
      });
    } finally {
      setPrintingId(null);
    }
  };

  return (
    <>
      <PageMeta title="Manure Sales | Poultry Book" description="Manure sales tracking" />
      <PageBreadcrumb pageTitle="Manure Sales" />

      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Manure Sales{" "}
            {total > 0 && (
              <span className="text-sm font-normal text-gray-500">({total})</span>
            )}
          </h3>
          {!isViewer && (
            <Button size="sm" onClick={openCreate}>
              Record Sale
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 px-5 pb-4 sm:px-6">
          <DateRangeFilter onApply={handleDateRangeApply} />
          <select
            value={filterBuyer}
            onChange={(e) => {
              setFilterBuyer(e.target.value);
              setOffset(0);
            }}
            className={`${selectClass} max-w-[200px]`}
          >
            <option value="">All Buyers</option>
            {filterBuyers.map((b) => (
              <option key={b.id} value={b.id}>{b.status === "active" ? b.name : `${b.name} (inactive)`}</option>
            ))}
          </select>
        </div>

        {error && !modalOpen && (
          <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">Loading...</div>
          ) : entries.length === 0 && !error ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              No manure sales in the selected period.
            </div>
          ) : entries.length === 0 ? null : (
            <Table>
              <TableHeader>
                <TableRow className="border-t border-gray-200 dark:border-gray-800">
                  <SortableHeader label="Date" sortKey="date" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader label="Qty" sortKey="quantity" sortConfig={sortConfig} onSort={requestSort} />
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Unit</TableCell>
                  <SortableHeader label="Rate/Unit" sortKey="rate" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader label="Total" sortKey="amount" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader label="Buyer" sortKey="buyer" sortConfig={sortConfig} onSort={requestSort} />
                  <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Notes</TableCell>
                  <TableCell isHeader className="px-5 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">{""}</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((sale) => (
                  <TableRow key={sale.id} className="border-t border-gray-100 dark:border-gray-800">
                    <TableCell className="px-5 py-3 text-sm text-gray-800 dark:text-white/90">
                      {new Date(sale.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-right font-medium text-gray-800 dark:text-white/90">
                      {sale.qty}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400 capitalize">
                      {sale.unit}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-right text-gray-600 dark:text-gray-400">
                      {formatCurrency(sale.rate_per_unit)}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-right font-medium text-gray-800 dark:text-white/90">
                      {formatCurrency(sale.total_amount)}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {sale.buyer_name || sale.buyer || "-"}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {sale.notes || "-"}
                    </TableCell>
                    <TableCell className="px-5 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        <PrintLink onClick={() => handlePrint(sale)} loading={printingId === sale.id} />
                        {!isViewer && (
                          <>
                            <button onClick={() => openEdit(sale)} className="text-brand-500 hover:text-brand-600 dark:text-brand-400 text-xs font-medium">Edit</button>
                            <button onClick={() => setDeleteConfirmId(sale.id)} className="text-error-500 hover:text-error-600 dark:text-error-400 text-xs font-medium">Delete</button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <Pagination total={total} limit={limit} offset={offset} onPrev={handlePrev} onNext={handleNext} />
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="max-w-md p-5 sm:p-8 mx-4">
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          {editingId ? "Edit Manure Sale" : "Record Manure Sale"}
        </h4>

        {error && modalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="msDate">Date *</Label>
            <Input id="msDate" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="msQty">Quantity *</Label>
              <Input id="msQty" type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="e.g. 5" required min={0.01} step="0.01" />
            </div>
            <div>
              <Label htmlFor="msUnit">Unit *</Label>
              <select id="msUnit" value={unit} onChange={(e) => setUnit(e.target.value)} className={selectClass}>
                {UNIT_OPTIONS.map((u) => (
                  <option key={u} value={u}>{u.charAt(0).toUpperCase() + u.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <Label htmlFor="msRate">Rate per Unit *</Label>
            <Input id="msRate" type="number" value={ratePerUnit} onChange={(e) => setRatePerUnit(e.target.value)} placeholder="e.g. 2000" required min={0.01} step="0.01" />
          </div>
          <div>
            <Label htmlFor="msTotal">Total Amount * <span className="text-xs text-gray-400">auto-calculated, editable</span></Label>
            <Input id="msTotal" type="number" value={totalAmount} onChange={(e) => setTotalAmount(e.target.value)} placeholder="e.g. 10000" required min={1} step="1" />
          </div>
          <div>
            <Label htmlFor="msBuyer">Buyer</Label>
            <select id="msBuyer" value={buyer} onChange={(e) => setBuyer(e.target.value)} className={selectClass}>
              <option value="">Select buyer (optional)</option>
              {buyers.map((b) => (
                <option key={b.id} value={String(b.id)}>{b.name}</option>
              ))}
              <option value={ADHOC_BUYER}>+ Other (type a name)</option>
            </select>
            {buyer === ADHOC_BUYER && (
              <div className="mt-2">
                <Input
                  type="text"
                  value={buyerName}
                  onChange={(e) => setBuyerName(e.target.value)}
                  placeholder="Buyer name"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  For one-time cash sales. Use the master buyer list to track unpaid amounts.
                </p>
              </div>
            )}
          </div>
          {showPayments && (
            <SalePaymentSection pay={pay} invoiceTotal={invoiceTotal} formatCurrency={formatCurrency} />
          )}

          {/* Creation only. On an edit this sale's own receivable is already in
              the buyer's outstanding balance, so adding the total again would
              double-count it — and UpdateSale carries no credit guard, so a
              "blocked" banner there would claim a control that doesn't exist.
              `amount` is the credit TAKEN, matching what the server guards on:
              asking about the gross total would block a sale the server allows,
              and the owner's approval tick would then silently do nothing. */}
          {!editingId && (
            <BuyerCreditNotice
              buyerId={buyer && buyer !== ADHOC_BUYER ? parseInt(buyer) : null}
              amount={showPayments ? pay.outstanding : invoiceTotal}
              override={creditOverride}
              onOverrideChange={setCreditOverride}
            />
          )}
          <div>
            <Label htmlFor="msNotes">Notes</Label>
            <textarea id="msNotes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" rows={2} className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" size="sm" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" size="sm" disabled={saving || !qty || !ratePerUnit || !totalAmount || (buyer === ADHOC_BUYER && !buyerName.trim()) || paymentsExceedTotal}>{saving ? "Saving..." : editingId ? "Update Sale" : "Record Sale"}</Button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={deleteConfirmId !== null}
        title="Delete Sale"
        message={
          <>
            {deleteSaleMessage("manure", entries.find((e) => e.id === deleteConfirmId))}
            {paidWarning && (
              <div className="mt-4 rounded-lg bg-warning-50 p-3 text-sm text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">
                {paidWarning}
              </div>
            )}
          </>
        }
        confirmLabel={paidWarning ? "Delete anyway" : "Delete"}
        busy={deleting}
        busyLabel="Deleting…"
        onConfirm={handleDelete}
        onClose={closeDelete}
      />
    </>
  );
}
